import { SelectOptionTypes } from "../../components/Input";

export type LiabilitiesResponse = {
  liabilities: LiabilitiesObject[];
  currentAmount: number;
  amountLent: number;
  paidAmount: number;
  principalAmount: number;
  interestAmount: number;
};

export type LiabilitiesTotals = Omit<LiabilitiesResponse, "liabilities">;

export type LiabilitiesObject = {
  id: number;
  name: string;
  isActive: boolean;
  amountLent: number;
  status: LiabilityStatus;
  startDate: string;
  endDate: string;
  finishDate?: string | null;
  liabilityTypeId: number;
  liabilityTypeName: string;
  currentAmount: number;
  paidAmount: number;
  principalAmount: number;
  interestAmount: number;
  rateType?: LiabilityRateTypes;
  interestRate?: number;
  paymentAmount?: number | null;
};

export enum LiabilityStatus {
  ACTIVE = "ACTIVE",
  CLOSED = "CLOSED",
  DEFAULTED = "DEFAULTED",
}

export interface LiabilityPageProps {
  id: number;
}

export type LiabilitiesDetails = {
  id: number;
  accountId: number;
  name: string;
  isActive: boolean;
  institution?: string;
  amountLent: number;
  status: LiabilityStatus;
  startDate: string;
  endDate: string;
  finishDate?: string | null;
  deactivationDate?: string;
  liabilityTypeId: number;
  liabilityTypeName: string;
  paymentFrequency?: LiabilityPaymentFrequency | null;
  paymentDueDay?: number | null;
  paymentAmount?: number | null;
  isApproximatedAmount: boolean;
  nextPaymentPrincipal: number | null;
  nextPaymentInterest: number | null;
  nextPaymentDate: string | null;
  isMissingPayment: boolean;
  currentAmount: number;
  paidAmount: number;
  principalAmount: number;
  interestAmount: number;
  lastPaymentDate: string | null;
  resetMonths: number | null;
  nextResetDate: string | null;
  interestRateHistory: LiabilityInterestRateHistory[];
};

export type LiabilityPayments = {
  id: number;
  paymentDate: string;
  amount: number;
  principalAmount: number;
  interestAmount: number;
  fromAccountId: number;
  fromAccountName: string;
  note?: string;
};

export interface LiabilityInterestRateHistory {
  id: number;
  interestRate: number;
  rateType: LiabilityRateTypes;
  effectiveDate: string;
  paymentAmount?: number | null;
  resetFrequency?: number | null;
}

export type LiabilityBalanceHistoryPoint = {
  date: string;
  balance: number;
};

export enum LiabilityPaymentStatus {
  SCHEDULED = "SCHEDULED",
  OVERPAYMENT = "OVERPAYMENT",
  LATE = "LATE",
}

export interface LiabilityPaymentHistoryEntry {
  id: number;
  date: string;
  amount: number;
  principalAmount: number;
  interestAmount: number;
  balanceAfter: number;
  status: LiabilityPaymentStatus;
  note?: string;
  fromAccountId?: number;
  fromAccountName?: string;
}

export type LiabilityBalanceHistory = {
  points: LiabilityBalanceHistoryPoint[];
  payments: LiabilityPaymentHistoryEntry[];
};

export interface LiabilityUpdateObject {
  name: string;
  institution?: string;
  amount: string;
  startDate: string;
  endDate: string;
  liabilityType: SelectOptionTypes;
  paymentFrequency?: SelectOptionTypes | null;
  paymentDueDay?: number | null;
}

export interface LiabilityUpdateRequest {
  name?: string;
  institution?: string;
  amount?: number;
  status?: LiabilityStatus;
  startDate?: string;
  endDate?: string;
  liabilityTypeId?: number;
  paymentFrequency?: LiabilityPaymentFrequency;
  paymentDueDay?: number;
}

export type LiabilityTypes = {
  id: number;
  name: string;
};

export enum LiabilityRateTypes {
  FIXED = "Fixed",
  VARIABLE = "Variable",
}

export enum LiabilityPaymentFrequency {
  WEEKLY = "WEEKLY",
  BIWEEKLY = "BIWEEKLY",
  MONTHLY = "MONTHLY",
  QUARTERLY = "QUARTERLY",
  SEMIANNUAL = "SEMIANNUAL",
  ANNUAL = "ANNUAL",
}

export type LiabilityRateTypesOptions = {
  id: LiabilityRateTypes;
  label: string;
};
